import React, { useRef } from 'react';

import SearchableMenu from 'components/base/Menu/SearchableMenu';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import AnimatedCaret from 'components/base/AnimatedCaret';
import colors from 'styles/themes/colors';
import CountryFlag from '../CountryFlag';
import { RecentFilter } from 'components/TopicFilters/TopicFilters.styles';

import { findString } from 'helpers/findString';
import styled, { css } from 'styled-components/macro';
import { ThemeProps } from 'types/styled';
import variables from 'styles/themes/variables';
import modularScale from 'styles/helpers/modular-scale';
import { useFilterSize } from 'components/TopicFilters/utils/useFilterSize';
import DropdownButton from '../TopicFiltersDropdown/DropdownButton';
import DropdownWrapper from '../TopicFiltersDropdown/DropdownWrapper';

const hoveredStyles = (isActive: any): any => css`
  background: ${colors.Sand};
  span {
    transform: ${!isActive && 'translateX(5px)'};
  }
`;

const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isActive, isHovered }): any => css`
    cursor: pointer;
    background: ${isActive || isHovered ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      ${hoveredStyles(isActive)}
    }

    ${isHovered && hoveredStyles(isActive)}
  `}
`;

export const ButtonBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isOpen }): any => css`
    @media screen and (max-width: 47em) {
      background: ${isOpen ? colors.Sand : 'white'};

      padding: ${({ theme }): string =>
        `${modularScale(-2)} ${theme.spacing.medium} ${modularScale(
          -2
        )} 0.75rem `};
    }
  `}
`;

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive, isHovered, isDisplayedWithFlag } = props;

  return (
    <ListItemBox
      isActive={isActive}
      isHovered={isHovered}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      paddingRight={2}
      paddingLeft={3}
      py={2}
    >
      <Box alignSelf="auto" display="inline-block">
        {isDisplayedWithFlag && (
          <CountryFlag
            small
            id={item.id ?? 'WW'}
            displayName={item.displayName}
            flagImgSmall={item.flagImgSmall}
            flagImgMedium={item.flagImgMedium}
            flagImgLarge={item.flagImgLarge}
          />
        )}
      </Box>
      <Span marginLeft={1} alignSelf="auto">
        {item.label}
      </Span>
      <RecentFilter>{item.isRecent ? 'Recent' : item.id}</RecentFilter>
    </ListItemBox>
  );
};

const Button: React.FC<any> = (props) => {
  const {
    selectedItem,
    selectedItems,
    isOpen,
    disabled,
    defaultLabel,
    isDisplayedWithFlag,
  } = props;

  const ref = useRef(null);
  const boxColor = disabled ? colors.DisabledGrey : colors.NightBlue;
  useFilterSize(ref);

  return (
    <ButtonBox
      isOpen={isOpen}
      bg={'white'}
      py={1}
      px={2}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      color={boxColor}
    >
      {selectedItems?.length > 1 ? (
        <Span
          alignSelf="auto"
          marginRight="8px"
          fontWeight={600}
          color={colors.NightBlue}
          style={{ whiteSpace: 'nowrap' }}
        >
          {`Countries (${selectedItems.length})`}
        </Span>
      ) : (
        <>
          <Box alignSelf="auto" display="inline-block">
            {selectedItem && isDisplayedWithFlag && (
              <CountryFlag
                small
                id={selectedItem.id ?? 'WW'}
                displayName={selectedItem.displayName}
                flagImgSmall={selectedItem.flagImgSmall}
                flagImgMedium={selectedItem.flagImgMedium}
                flagImgLarge={selectedItem.flagImgLarge}
              />
            )}
          </Box>
          <Span alignSelf="auto" marginRight="8px" fontWeight={600} ref={ref}>
            {selectedItem ? selectedItem.label : defaultLabel}
          </Span>
        </>
      )}
      <Span marginLeft="auto" alignSelf="auto">
        <AnimatedCaret isPointingUp={isOpen} />
      </Span>
    </ButtonBox>
  );
};

const getButtonWithDefaultLabel = (
  clearFilter?: () => void,
  defaultLabel?: string,
  withoutArrow?: boolean
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return clearFilter ? (
      <DropdownButton
        {...props}
        defaultLabel={defaultLabel}
        dropdownType="Territory"
        clearFilter={clearFilter}
        withoutArrow={withoutArrow}
      />
    ) : (
      <Button {...props} defaultLabel={defaultLabel} />
    );
  };
  return LabeledButton;
};

const CountryDropdown: React.FC<ICountryDropdownProps> = (props) => {
  const {
    items,
    selectedItem,
    onChange,
    disabled,
    itemToString,
    defaultLabel,
    children,
    isDisplayedWithFlag = true,
    isTooltipEnabled,
    tooltipContent,
    disabledMessage,
    maxWidth,
    placement,
    zIndex,
    isStretched,
    clearFilter,
    isTopicFilter,
    checkIfOnlyOneValue,
  } = props;
  const matchByCountryId = (item: any, searchValue: string): boolean => {
    return findString(searchValue)(item.id);
  };

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.

  const hasOnlyOneValue = items && items.length === 1;

  return isTopicFilter ? (
    <DropdownWrapper label="TERRITORY">
      <SearchableMenu
        items={items}
        selectedItem={selectedItem || null}
        onChange={onChange}
        disabled={disabled}
        disabledMessage={disabledMessage}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(
          clearFilter,
          defaultLabel,
          checkIfOnlyOneValue && hasOnlyOneValue
        )}
        Item={ListItem}
        additionalFindMethod={matchByCountryId}
        isDisplayedWithFlag={isDisplayedWithFlag}
        isTooltipEnabled={isTooltipEnabled}
        tooltipContent={tooltipContent}
        maxWidth={maxWidth}
        placement={placement}
        zIndex={zIndex}
        isMenuButtonStretched={isStretched}
        onlyOneValue={hasOnlyOneValue}
      >
        {children}
      </SearchableMenu>
    </DropdownWrapper>
  ) : (
    <SearchableMenu
      items={items}
      selectedItem={selectedItem || null}
      onChange={onChange}
      disabled={disabled}
      disabledMessage={disabledMessage}
      itemToString={itemToString}
      Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
      Item={ListItem}
      additionalFindMethod={matchByCountryId}
      isDisplayedWithFlag={isDisplayedWithFlag}
      isTooltipEnabled={isTooltipEnabled}
      tooltipContent={tooltipContent}
      maxWidth={maxWidth}
      placement={placement}
      zIndex={zIndex}
      isMenuButtonStretched={isStretched}
    >
      {children}
    </SearchableMenu>
  );
};

interface ICountryDropdownProps {
  items: any[];
  selectedItem?: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  disabledMessage?: string;
  itemToString?: (item: any) => string | null;
  defaultLabel?: string;
  children?: any;
  isDisplayedWithFlag?: boolean;
  isTooltipEnabled?: boolean;
  tooltipContent?: string;
  /** Max width of the tooltip for the dropdown, zIndex of the tooltip block,
   * placement is for place of tooltip relatively the block, isStretched is to stretch the block in tooltip wrapper */
  maxWidth?: string;
  placement?: string;
  zIndex?: string;
  isStretched?: boolean;
  clearFilter?: () => void;
  isTopicFilter?: boolean;
  checkIfOnlyOneValue?: boolean;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
  isDisplayedWithFlag?: boolean;
}

export default CountryDropdown;
